<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { toast } from 'dashboard/composables/useToast';
import { uploadFile } from 'dashboard/helper/uploadHelper';

import Input from 'dashboard/components-next/input/Input.vue';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  // Contato da conversa atual — habilita preencher variaveis com campos do contato.
  contact: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();

const processedParams = ref({});
const isUploadingMedia = ref(false);
// Por variavel do corpo: 'fixed' (texto livre) ou a chave de um campo do contato.
const bodyVarSource = ref({});

// Chave por template para lembrar a última imagem usada (URL pública nossa).
const mediaStorageKey = computed(
  () => `wa_tpl_media:${props.template.name}:${props.template.language}`
);

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

// Cabeçalho de texto com variável ({{1}})
const hasTextHeaderVar = computed(
  () =>
    headerComponent.value?.format === 'TEXT' &&
    /{{\s*\d+\s*}}/.test(headerComponent.value?.text || '')
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

// Tipos de arquivo aceitos no upload conforme o formato do cabeçalho.
const mediaAccept = computed(() => {
  const format = headerComponent.value?.format?.toUpperCase();
  if (format === 'VIDEO') return 'video/mp4';
  if (format === 'DOCUMENT') return 'application/pdf';
  return 'image/jpeg,image/png';
});

const hasVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

// Oferta por tempo limitado (LTO)
const ltoComponent = computed(() =>
  props.template.components?.find(c => c.type === 'LIMITED_TIME_OFFER')
);
const isLtoTemplate = computed(() => !!ltoComponent.value);
const ltoHasExpiration = computed(
  () => ltoComponent.value?.limited_time_offer?.has_expiration !== false
);

// Autenticação (OTP)
const isAuthTemplate = computed(() => {
  if (props.template.category === 'AUTHENTICATION') return true;
  const buttons = props.template.components?.find(c => c.type === 'BUTTONS');
  return (buttons?.buttons || []).some(b => b.type === 'OTP');
});

// Carrossel
const carouselComponent = computed(() =>
  props.template.components?.find(c => c.type === 'CAROUSEL')
);
const isCarouselTemplate = computed(() => !!carouselComponent.value);
const carouselCards = computed(() => carouselComponent.value?.cards || []);

const cardSampleImage = card => {
  const header = card.components?.find(c => c.type === 'HEADER');
  return header?.example?.header_handle?.[0] || '';
};
const cardBodyText = card =>
  card.components?.find(c => c.type === 'BODY')?.text || '';

const hasContact = computed(() => !!props.contact?.id);

// Campos do contato oferecidos para preencher variaveis ({{1}} = Nome, etc.).
const contactFieldOptions = computed(() => {
  if (!hasContact.value) return [];
  const base = [
    {
      value: 'name',
      label: t('WHATSAPP_TEMPLATES.PARSER.CONTACT_FIELDS.NAME'),
    },
    {
      value: 'email',
      label: t('WHATSAPP_TEMPLATES.PARSER.CONTACT_FIELDS.EMAIL'),
    },
    {
      value: 'phone_number',
      label: t('WHATSAPP_TEMPLATES.PARSER.CONTACT_FIELDS.PHONE'),
    },
  ];
  const customAttrs = props.contact?.custom_attributes || {};
  Object.keys(customAttrs).forEach(key =>
    base.push({ value: key, label: key })
  );
  return base;
});

const resolveContactField = field => {
  const contact = props.contact || {};
  if (['name', 'email', 'phone_number'].includes(field)) {
    return contact[field] || '';
  }
  const customAttrs = contact.custom_attributes || {};
  const additionalAttrs = contact.additional_attributes || {};
  const value = customAttrs[field] ?? additionalAttrs[field];
  return value == null ? '' : String(value);
};

// "{{1}}" montado no script: chaves literais dentro da interpolacao
// encerrariam o mustache do Vue no lugar errado.
const varToken = key => `{{${key}}}`;

const onVarSourceChange = key => {
  const source = bodyVarSource.value[key];
  if (source && source !== 'fixed') {
    processedParams.value.body[key] = resolveContactField(source);
  } else {
    processedParams.value.body[key] = '';
  }
};

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

const isFormInvalid = computed(() => {
  if (hasMediaHeader.value && !processedParams.value.header?.media_url) {
    return true;
  }

  if (hasTextHeaderVar.value && !processedParams.value.header?.[1]) {
    return true;
  }

  if (hasVariables.value && processedParams.value.body) {
    const hasEmptyBodyVariable = Object.values(processedParams.value.body).some(
      value => !value
    );
    if (hasEmptyBodyVariable) return true;
  }

  if (processedParams.value.buttons) {
    const hasEmptyButtonParameter = processedParams.value.buttons.some(
      button => !button.parameter
    );
    if (hasEmptyButtonParameter) return true;
  }

  if (
    isLtoTemplate.value &&
    ltoHasExpiration.value &&
    !processedParams.value.lto?.expires_in_hours
  ) {
    return true;
  }

  if (isCarouselTemplate.value) {
    const cards = processedParams.value.carousel?.cards || [];
    if (cards.some(card => !card.media_url)) return true;
  }

  if (isAuthTemplate.value && !processedParams.value.auth?.code) {
    return true;
  }

  return false;
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );
  // Cabeçalho de texto com variável: input vazio.
  if (hasTextHeaderVar.value) {
    processedParams.value.header = { 1: '' };
  }
  // Autenticação: código OTP.
  if (isAuthTemplate.value) {
    processedParams.value.auth = { code: '' };
  }
  // Oferta por tempo limitado: prazo padrão de 24h (editável).
  if (isLtoTemplate.value && ltoHasExpiration.value) {
    processedParams.value.lto = { expires_in_hours: 24 };
  }
  // Carrossel: cada card já vem com a imagem de exemplo aprovada (editável).
  if (isCarouselTemplate.value) {
    processedParams.value.carousel = {
      cards: carouselCards.value.map(card => ({
        media_url: cardSampleImage(card),
        media_type: 'image',
      })),
    };
  }
  // Toda variavel começa como texto fixo (comportamento atual).
  bodyVarSource.value = {};
  if (processedParams.value.body) {
    Object.keys(processedParams.value.body).forEach(key => {
      bodyVarSource.value[key] = 'fixed';
    });
  }
  // Auto-preenche a imagem: última usada (localStorage) ou, na falta dela,
  // a imagem de exemplo aprovada na Meta (header_handle).
  if (hasMediaHeader.value) {
    let saved = '';
    try {
      saved = window.localStorage.getItem(mediaStorageKey.value) || '';
    } catch {
      saved = '';
    }
    const sample = headerComponent.value?.example?.header_handle?.[0] || '';
    const prefill = saved || sample;
    if (prefill && !processedParams.value.header?.media_url) {
      processedParams.value.header ??= {};
      processedParams.value.header.media_url = prefill;
    }
  }
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

// Sobe a imagem de um card do carrossel pro nosso storage.
const onCarouselCardSelect = async (index, event) => {
  const file = event.target.files?.[0];
  if (!file) return;
  isUploadingMedia.value = true;
  try {
    const { fileUrl } = await uploadFile(file);
    processedParams.value.carousel.cards[index].media_url = fileUrl;
  } catch {
    toast.error(t('WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_ERROR'));
  } finally {
    isUploadingMedia.value = false;
  }
};

// Sobe o arquivo pro nosso storage (URL pública), preenche e lembra por template.
const onMediaFileSelect = async event => {
  const file = event.target.files?.[0];
  if (!file) return;
  isUploadingMedia.value = true;
  try {
    const { fileUrl } = await uploadFile(file);
    updateMediaUrl(fileUrl);
    if (isDocumentTemplate.value) updateMediaName(file.name);
    try {
      window.localStorage.setItem(mediaStorageKey.value, fileUrl);
    } catch {
      // ignora erro de storage
    }
  } catch {
    toast.error(t('WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_ERROR'));
  } finally {
    isUploadingMedia.value = false;
  }
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: processedParams.value,
    },
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedTemplate,
  v$,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div
      v-if="
        hasVariables ||
        hasMediaHeader ||
        hasTextHeaderVar ||
        processedParams.buttons ||
        isLtoTemplate ||
        isCarouselTemplate ||
        isAuthTemplate
      "
    >
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>
        <label
          class="inline-flex justify-center items-center gap-2 px-3 py-2 mb-2 w-full text-sm font-medium rounded-lg border border-dashed cursor-pointer border-n-strong text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
        >
          <span
            v-if="isUploadingMedia"
            class="i-lucide-loader-2 size-4 animate-spin"
          />
          <span v-else class="i-lucide-upload size-4" />
          {{
            isUploadingMedia
              ? t('WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOADING')
              : t('WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_LABEL')
          }}
          <input
            type="file"
            :accept="mediaAccept"
            class="hidden"
            :disabled="isUploadingMedia"
            @change="onMediaFileSelect"
          />
        </label>
        <p
          v-if="processedParams.header?.media_url"
          class="mb-2 text-xs text-n-teal-11"
        >
          {{ t('WHATSAPP_TEMPLATES.PARSER.MEDIA_READY') }}
        </p>
        <div class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="updateMediaUrl"
          />
        </div>
        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Autenticação: código OTP -->
      <div v-if="isAuthTemplate && processedParams.auth" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.AUTH_CODE_LABEL') }}
        </p>
        <Input
          v-model="processedParams.auth.code"
          type="text"
          class="w-full"
          :placeholder="t('WHATSAPP_TEMPLATES.PARSER.AUTH_CODE_PLACEHOLDER')"
        />
      </div>

      <!-- Cabeçalho de texto com variável -->
      <div v-if="hasTextHeaderVar && processedParams.header" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.HEADER_VAR_LABEL') }}
        </p>
        <Input
          v-model="processedParams.header[1]"
          type="text"
          class="w-full"
          :placeholder="t('WHATSAPP_TEMPLATES.PARSER.HEADER_VAR_PLACEHOLDER')"
        />
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex gap-2 items-center mb-2.5"
        >
          <!-- Selo da variavel: liga a linha ao {{n}} do texto acima -->
          <span
            class="shrink-0 w-10 text-center px-1 py-1 rounded-md bg-n-alpha-2 text-[11px] font-medium tabular-nums text-n-slate-10"
          >
            {{ varToken(key) }}
          </span>
          <!-- reset-base: sem ele o CSS global (select:not(.reset-base))
               forca width 100% + mb-4 e o select engolia a linha inteira,
               esmagando o input ao lado. -->
          <select
            v-if="hasContact"
            v-model="bodyVarSource[key]"
            class="reset-base h-10 px-3 text-sm rounded-lg outline outline-1 outline-offset-[-1px] outline-n-weak hover:outline-n-slate-6 bg-n-alpha-black2 text-n-slate-12 w-44 shrink-0 cursor-pointer transition-all"
            @change="onVarSourceChange(key)"
          >
            <option value="fixed">
              {{ t('WHATSAPP_TEMPLATES.PARSER.SOURCE_FIXED') }}
            </option>
            <option
              v-for="opt in contactFieldOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1 min-w-0"
            :disabled="hasContact && bodyVarSource[key] !== 'fixed'"
            :placeholder="
              hasContact && bodyVarSource[key] !== 'fixed'
                ? t('WHATSAPP_TEMPLATES.PARSER.SOURCE_AUTO_PLACEHOLDER')
                : t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                    variable: key,
                  })
            "
          />
        </div>
      </div>

      <!-- Carrossel: imagem por card -->
      <div v-if="isCarouselTemplate && processedParams.carousel">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.CAROUSEL_LABEL') }}
        </p>
        <div
          v-for="(card, index) in processedParams.carousel.cards"
          :key="`card-${index}`"
          class="flex gap-3 items-center p-2 mb-2 rounded-lg border border-n-weak"
        >
          <img
            v-if="card.media_url"
            :src="card.media_url"
            class="object-cover w-14 h-14 rounded shrink-0"
            alt=""
          />
          <div
            v-else
            class="flex justify-center items-center w-14 h-14 rounded shrink-0 bg-n-alpha-2"
          >
            <span class="i-lucide-image size-5 text-n-slate-10" />
          </div>
          <div class="flex flex-col flex-1 gap-1 min-w-0">
            <span class="text-xs truncate text-n-slate-11">
              {{ cardBodyText(carouselCards[index]) }}
            </span>
            <label
              class="inline-flex gap-1.5 items-center text-xs font-medium cursor-pointer text-n-teal-11"
            >
              <span class="i-lucide-upload size-3.5" />
              {{ t('WHATSAPP_TEMPLATES.PARSER.CAROUSEL_CHANGE_IMAGE') }}
              <input
                type="file"
                accept="image/jpeg,image/png"
                class="hidden"
                :disabled="isUploadingMedia"
                @change="onCarouselCardSelect(index, $event)"
              />
            </label>
          </div>
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="
              button?.type === 'copy_code'
                ? t('WHATSAPP_TEMPLATES.PARSER.COUPON_PLACEHOLDER')
                : t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')
            "
          />
        </div>
      </div>

      <!-- Oferta por tempo limitado (expiração) -->
      <div v-if="isLtoTemplate && ltoHasExpiration && processedParams.lto">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.LTO_EXPIRY_LABEL') }}
        </p>
        <div class="flex gap-2 items-center mb-1">
          <Input
            v-model.number="processedParams.lto.expires_in_hours"
            type="number"
            min="1"
            class="w-24"
          />
          <span class="text-sm text-n-slate-11">
            {{ t('WHATSAPP_TEMPLATES.PARSER.LTO_EXPIRY_HOURS') }}
          </span>
        </div>
        <p class="text-xs text-n-slate-10">
          {{ t('WHATSAPP_TEMPLATES.PARSER.LTO_EXPIRY_HINT') }}
        </p>
      </div>

      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
